import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { FiAlertTriangle } from "react-icons/fi";
import Breadcrumbs from "../../../components/Breadcrumbs";
import NewTableComponent from "../../../components/NewTableComponent";
import { apiService } from "../../../services/apiService";
import SpinnerLoader from "../../../utils/SpinnerLoader";
import { errorNotify, successNotify } from "../../../utils/toast";

import "../../../styles/Access.css";
import "../../../styles/Button.css";
import "../../../styles/FinancialReports.css";
import "../../../styles/Input.css";
import "../../../styles/Issue.css";
import "../../../styles/Services.css";
import "../../../styles/Table.css";
import "../../../styles/Trip.css";
import "../../../styles/TripCostEstimation.css";

const resolveApiFileUrl = (value = "") => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return "";
  if (/^(https?:|blob:|data:)/i.test(normalizedValue)) return normalizedValue;
  return `${process.env.REACT_APP_API_FILES_URL || ""}${normalizedValue}`;
};

// Custom inline SVG icons matching the design
const BanknoteIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="5" width="20" height="14" rx="2" fill="#22C55E" fillOpacity="0.2" stroke="#22C55E" strokeWidth="2" />
    <circle cx="12" cy="12" r="3" stroke="#22C55E" strokeWidth="2" />
    <path d="M6 9H7M17 15H18" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CostsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 12H18L15 21L9 3L6 12H2" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ProfitIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23 6L13.5 15.5L8.5 10.5L1 18" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 6H23V12" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MarginIcon = () => (
  <span style={{ fontSize: "24px", fontWeight: "700", color: "#64748B", fontFamily: "monospace" }}>%</span>
);

const generateDummyReportData = (type, startDateVal, endDateVal, customerVal, customerLabel) => {
  const generatedTime = moment().format("YYYY-MM-DD HH:mm:ss");
  const formattedRange = `Range: ${startDateVal || "2026-06-01"} to ${endDateVal || "2026-06-30"}`;

  if (type === "pnl") {
    return {
      type: "pnl",
      typeLabel: "P&L",
      title: "Profit & Loss Report",
      range: formattedRange,
      generatedInfo: `Generated by AJCL Admin on ${generatedTime}`,
      cards: [
        { label: "PAID REVENUE", amount: "PKR 65,850.00", subtitle: "Cash basis", color: "green", icon: <BanknoteIcon /> },
        { label: "DIRECT COSTS", amount: "PKR 382,800.00", subtitle: "Fuel+Toll+Maint+Salary", color: "red", icon: <CostsIcon /> },
        { label: "NET PROFIT", amount: "PKR -316,950.00", subtitle: "Revenue - costs", color: "blue", icon: <ProfitIcon /> },
        { label: "MARGIN", amount: "-481.3%", subtitle: "Operating margin", color: "gray", icon: <MarginIcon /> }
      ],
      warnings: [
        { customer: "ICL", message: "ICL: This customer has costs in the selected period but no paid revenue. Unpaid invoices may still be sitting in AR." },
        { customer: "T&T", message: "T&T: This customer has costs in the selected period but no paid revenue. Unpaid invoices may still be sitting in AR." }
      ],
      tableTitle: "Customer-Wise Revenue & Contribution (cash basis)",
      tableColumns: [
        { name: "CUSTOMER", selector: (row) => row.customer, minWidth: "150px" },
        { name: "PAID REVENUE", selector: (row) => `PKR ${row.paidRevenue.toLocaleString()}`, minWidth: "150px" },
        { name: "FUEL", selector: (row) => `PKR ${row.fuel.toLocaleString()}`, minWidth: "150px" },
        { name: "TOLLS", selector: (row) => `PKR ${row.tolls.toLocaleString()}`, minWidth: "120px" },
        { name: "MAINTENANCE", selector: (row) => `PKR ${row.maintenance.toLocaleString()}`, minWidth: "150px" },
        {
          name: "CONTRIBUTION", selector: (row) => (
            <span style={{ color: row.contribution < 0 ? "#B42318" : "#027A48", fontWeight: 600 }}>
              PKR {row.contribution.toLocaleString()}
            </span>
          ), minWidth: "150px"
        },
        {
          name: "MARGIN", selector: (row) => (
            <span style={{ color: row.marginVal < 0 ? "#B42318" : "#027A48", fontWeight: 600 }}>
              {row.margin}
            </span>
          ), minWidth: "100px"
        }
      ],
      tableData: [
        { customer: "Colgate", paidRevenue: 50850, fuel: 106500, tolls: 7300, maintenance: 22000, contribution: -84950, marginVal: -167.1, margin: "-167.1%" },
        { customer: "ICL", paidRevenue: 0, fuel: 76000, tolls: 5000, maintenance: 8000, contribution: -89900, marginVal: 0, margin: "—" },
        { customer: "Hamza Mughal", paidRevenue: 15000, fuel: 125000, tolls: 9000, maintenance: 18000, contribution: -137000, marginVal: -913.3, margin: "-913.3%" },
        { customer: "T&T", paidRevenue: 0, fuel: 5500, tolls: 500, maintenance: 0, contribution: -6000, marginVal: 0, margin: "—" }
      ],
      tableNote: "Paid Revenue comes from recorded payments only. Unpaid invoice amounts are not counted as revenue. Driver salaries are company-wide and shown in the summary below, not split per customer.",
      companySummary: {
        title: "Company-Wide Summary",
        rows: [
          { label: "Total Paid Revenue", value: "PKR 65,850.00", isGreen: true },
          { label: "- Total Costs (Fuel + Toll + Maintenance + Salaries)", value: "PKR 382,800.00" },
          { label: "• Driver Salaries (locked payroll)", value: "PKR 0.00", isIndented: true },
          { label: "Net Profit", value: "PKR -316,950.00", isBlue: true }
        ],
        note: "Revenue is recognized when payment is recorded. Driver salaries are company-wide (not split per customer) and only counted from Locked payroll runs."
      }
    };
  } else if (type === "ar") {
    return {
      type: "ar",
      typeLabel: "AR",
      title: "Accounts Receivable Report",
      range: formattedRange,
      generatedInfo: `Generated by AJCL Admin on ${generatedTime}`,
      cards: [
        { label: "TOTAL RECEIVABLES", amount: "PKR 662,000.00", subtitle: "Outstanding", color: "blue", icon: <ProfitIcon /> },
        { label: "CURRENT (1-30 DAYS)", amount: "PKR 310,000.00", subtitle: "Within term", color: "green", icon: <BanknoteIcon /> },
        { label: "OVERDUE (>30 DAYS)", amount: "PKR 352,000.00", subtitle: "Action required", color: "red", icon: <CostsIcon /> },
        { label: "AGING RATIO", amount: "53.2%", subtitle: "Overdue %", color: "gray", icon: <MarginIcon /> }
      ],
      warnings: [
        { customer: "T&T", message: "T&T: Accounts receivable has surpassed 90 days threshold. Payment collection is overdue." }
      ],
      tableTitle: "Customer Aging Analysis (open invoices)",
      tableColumns: [
        { name: "CUSTOMER", selector: (row) => row.customer, minWidth: "150px" },
        { name: "OUTSTANDING AMOUNT", selector: (row) => `PKR ${row.outstanding.toLocaleString()}`, minWidth: "180px" },
        { name: "1-30 DAYS", selector: (row) => `PKR ${row.days1_30.toLocaleString()}`, minWidth: "150px" },
        { name: "31-60 DAYS", selector: (row) => `PKR ${row.days31_60.toLocaleString()}`, minWidth: "150px" },
        { name: "61-90 DAYS", selector: (row) => `PKR ${row.days61_90.toLocaleString()}`, minWidth: "150px" },
        {
          name: "OVER 90 DAYS", selector: (row) => (
            <span style={{ color: row.daysOver90 > 0 ? "#B42318" : "#3D3F5C", fontWeight: row.daysOver90 > 0 ? 600 : 400 }}>
              PKR {row.daysOver90.toLocaleString()}
            </span>
          ), minWidth: "150px"
        },
        { name: "LAST INVOICED DATE", selector: (row) => row.lastInvoiced, minWidth: "180px" }
      ],
      tableData: [
        { customer: "Colgate", outstanding: 120000, days1_30: 80000, days31_60: 40000, days61_90: 0, daysOver90: 0, lastInvoiced: "2026-06-25" },
        { customer: "ICL", outstanding: 450000, days1_30: 150000, days31_60: 150000, days61_90: 150000, daysOver90: 0, lastInvoiced: "2026-06-20" },
        { customer: "Hamza Mughal", outstanding: 80000, days1_30: 80000, days31_60: 0, days61_90: 0, daysOver90: 0, lastInvoiced: "2026-06-28" },
        { customer: "T&T", outstanding: 120000, days1_30: 0, days31_60: 0, days61_90: 0, daysOver90: 120000, lastInvoiced: "2026-05-15" }
      ],
      tableNote: "Outstanding amounts include all approved, sent, and partially paid invoices. Drafted or cancelled invoices are excluded.",
      companySummary: {
        title: "Receivables Summary",
        rows: [
          { label: "Total Accounts Receivable Balance", value: "PKR 662,000.00", isGreen: true },
          { label: "- Current Receivables (1-30 Days)", value: "PKR 310,000.00" },
          { label: "- Overdue Receivables (31-90 Days)", value: "PKR 190,000.00", isIndented: true },
          { label: "- Critical Aging Receivables (>90 Days)", value: "PKR 120,000.00", isIndented: true, isBlue: true }
        ],
        note: "Receivables aging is calculated from the invoice issue date. Please follow up on balances older than 30 days."
      }
    };
  } else {
    return {
      type: "ap",
      typeLabel: "AP",
      title: "Accounts Payable Report",
      range: formattedRange,
      generatedInfo: `Generated by AJCL Admin on ${generatedTime}`,
      cards: [
        { label: "TOTAL PAYABLES", amount: "PKR 382,800.00", subtitle: "Accumulated payables", color: "red", icon: <CostsIcon /> },
        { label: "SUPPLIER BALANCE", amount: "PKR 232,800.00", subtitle: "PSO + Maintenance", color: "blue", icon: <ProfitIcon /> },
        { label: "PAYROLL BALANCE", amount: "PKR 150,000.00", subtitle: "Driver payouts", color: "green", icon: <BanknoteIcon /> },
        { label: "OVERDUE RATIO", amount: "2.0%", subtitle: "Due this week", color: "gray", icon: <MarginIcon /> }
      ],
      warnings: [
        { customer: "PSO Station", message: "PSO Station: fuel invoice of PKR 180,000.00 is due in 3 days. Prepare payout to avoid service disruption." }
      ],
      tableTitle: "Supplier & Vendor Payable Analysis",
      tableColumns: [
        { name: "SUPPLIER / VENDOR", selector: (row) => row.supplier, minWidth: "180px" },
        { name: "TOTAL PAYABLE", selector: (row) => `PKR ${row.outstanding.toLocaleString()}`, minWidth: "150px" },
        { name: "FUEL PURCHASE", selector: (row) => `PKR ${row.fuel.toLocaleString()}`, minWidth: "150px" },
        { name: "VEHICLE MAINTENANCE", selector: (row) => `PKR ${row.maintenance.toLocaleString()}`, minWidth: "180px" },
        { name: "DRIVER PAYOUTS", selector: (row) => `PKR ${row.payouts.toLocaleString()}`, minWidth: "150px" },
        { name: "DUE DATE", selector: (row) => row.dueDate, minWidth: "150px" }
      ],
      tableData: [
        { supplier: "PSO Station", outstanding: 180000, fuel: 180000, maintenance: 0, payouts: 0, dueDate: "2026-07-05" },
        { supplier: "Al Imran Spares", outstanding: 4500, fuel: 0, maintenance: 4500, payouts: 0, dueDate: "2026-07-10" },
        { supplier: "Driver Salaries", outstanding: 150000, fuel: 0, maintenance: 0, payouts: 150000, dueDate: "2026-07-01" },
        { supplier: "Shell Lubricants", outstanding: 7800, fuel: 0, maintenance: 7800, payouts: 0, dueDate: "2026-06-30" }
      ],
      tableNote: "Payables tracking includes unpaid fuel log purchases, locked maintenance items, and pending driver salaries.",
      companySummary: {
        title: "Payables Summary",
        rows: [
          { label: "Total Outstanding Accounts Payable", value: "PKR 382,800.00", isBlue: true },
          { label: "- Vendor & Supplier Liabilities", value: "PKR 232,800.00" },
          { label: "- Unpaid Driver Salaries", value: "PKR 150,000.00", isIndented: true, isGreen: true }
        ],
        note: "Vendor balances are updated from registered logs. Payout processing takes 2-3 business days."
      }
    };
  }
};

const FinancialReportDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const typeParam = queryParams.get("type"); // 'pnl' or 'ar'
  const [loading, setLoading] = useState(true);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadReportDetails = async () => {
      setLoading(true);
      try {
        let reportDetail = null;
        let isArDetail = false;

        if (typeParam === "ar") {
          try {
            const detailRes = await apiService.getFinancialArReportDetail(id);
            reportDetail = detailRes?.data || detailRes;
            if (reportDetail) {
              isArDetail = true;
            }
          } catch (e) {
            console.warn("Failed to fetch AR details", e);
          }
        } else if (typeParam === "pnl") {
          try {
            const detailRes = await apiService.getFinancialPnlReportDetail(id);
            reportDetail = detailRes?.data || detailRes;
          } catch (e) {
            console.warn("Failed to fetch P&L details", e);
          }
        } else {
          try {
            const detailRes = await apiService.getFinancialPnlReportDetail(id);
            reportDetail = detailRes?.data || detailRes;
          } catch (e) {
            console.warn("Report not found in P&L details, trying AR details", e);
          }

          if (!reportDetail) {
            try {
              const detailRes = await apiService.getFinancialArReportDetail(id);
              reportDetail = detailRes?.data || detailRes;
              if (reportDetail) {
                isArDetail = true;
              }
            } catch (e) {
              console.warn("Report not found in AR details, falling back to paginated scan", e);
            }
          }
        }

        if (reportDetail) {
          const generatedTime = reportDetail.created_at ? moment(reportDetail.created_at).format("YYYY-MM-DD HH:mm:ss") : moment().format("YYYY-MM-DD HH:mm:ss");
          const rawStart = reportDetail.start_date || "";
          const rawEnd = reportDetail.end_date || "";
          const formattedRange = `Range: ${rawStart ? moment(rawStart).format("YYYY-MM-DD") : "2026-06-01"} to ${rawEnd ? moment(rawEnd).format("YYYY-MM-DD") : "2026-06-30"}`;
          const creatorName = reportDetail.user?.username || reportDetail.user?.name || "Admin";

          if (isArDetail) {
            const detailsArray = Array.isArray(reportDetail.details) 
              ? reportDetail.details 
              : (reportDetail.details ? [reportDetail.details] : []);

            const totalOutstanding = detailsArray.reduce((sum, item) => sum + Number(item.outstanding_amount || 0), 0);
            const currentAmount = detailsArray.reduce((sum, item) => sum + Number(item.net_amount || 0), 0);

            const dynamicReport = {
              type: "ar",
              typeLabel: "AR",
              title: "Accounts Receivable Report",
              range: formattedRange,
              generatedInfo: `Generated by ${creatorName} on ${generatedTime}`,
              cards: [
                { label: "TOTAL RECEIVABLES", amount: `PKR ${totalOutstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, subtitle: "Outstanding", color: "blue", icon: <ProfitIcon /> },
                { label: "CURRENT (1-30 DAYS)", amount: `PKR ${currentAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, subtitle: "Within term", color: "green", icon: <BanknoteIcon /> },
                { label: "OVERDUE (>30 DAYS)", amount: "PKR 0.00", subtitle: "Action required", color: "red", icon: <CostsIcon /> },
                { label: "AGING RATIO", amount: "0.0%", subtitle: "Overdue %", color: "gray", icon: <MarginIcon /> }
              ],
              tableTitle: "Customer Aging Analysis (open invoices)",
              tableColumns: [
                { name: "CUSTOMER", selector: (row) => row.customer, minWidth: "180px" },
                { name: "OUTSTANDING AMOUNT", selector: (row) => `PKR ${Number(row.outstanding).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, minWidth: "180px" },
                { name: "1-30 DAYS", selector: (row) => `PKR ${Number(row.days1_30).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, minWidth: "150px" },
                { name: "31-60 DAYS", selector: (row) => `PKR ${Number(row.days31_60).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, minWidth: "150px" },
                { name: "61-90 DAYS", selector: (row) => `PKR ${Number(row.days61_90).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, minWidth: "150px" },
                {
                  name: "OVER 90 DAYS", selector: (row) => (
                    <span style={{ color: Number(row.daysOver90) > 0 ? "#B42318" : "#3D3F5C", fontWeight: Number(row.daysOver90) > 0 ? 600 : 400 }}>
                      PKR {Number(row.daysOver90).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  ), minWidth: "150px"
                },
                { name: "LAST INVOICED DATE", selector: (row) => row.lastInvoiced, minWidth: "180px" }
              ],
              tableData: detailsArray.map(item => ({
                customer: item.customer?.name || `Customer #${item.customer_id}`,
                outstanding: Number(item.outstanding_amount || 0),
                days1_30: Number(item.net_amount || 0),
                days31_60: 0,
                days61_90: 0,
                daysOver90: 0,
                lastInvoiced: item.created_at ? moment(item.created_at).format("YYYY-MM-DD") : "—"
              })),
              tableNote: "Outstanding amounts are updated dynamically based on open customer invoices.",
            };

            if (isMounted) {
              setGeneratedReport(dynamicReport);
            }
            return;
          }

          const dynamicReport = {
            type: "pnl",
            typeLabel: "P&L",
            title: "Profit & Loss Report",
            range: formattedRange,
            generatedInfo: `Generated by ${creatorName} on ${generatedTime}`,
            cards: [
              { label: "PAID REVENUE", amount: `PKR ${Number(reportDetail.total_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, subtitle: "Cash basis", color: "green", icon: <BanknoteIcon /> },
              { label: "DIRECT COSTS", amount: `PKR ${Number(reportDetail.total_expense || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, subtitle: "Fuel+Toll+Maint+Salary", color: "red", icon: <CostsIcon /> },
              { label: "NET PROFIT", amount: `PKR ${Number(reportDetail.net_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, subtitle: "Revenue - costs", color: Number(reportDetail.net_profit || 0) < 0 ? "red" : "blue", icon: <ProfitIcon /> },
              { label: "MARGIN", amount: `${Number(reportDetail.profit_margin || 0).toFixed(1)}%`, subtitle: "Operating margin", color: "gray", icon: <MarginIcon /> }
            ],
            tableTitle: "Customer-Wise Revenue & Contribution (cash basis)",
            tableColumns: [
              { name: "CUSTOMER", selector: (row) => row.customerName, minWidth: "180px" },
              { name: "PAID REVENUE", selector: (row) => `PKR ${Number(row.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, minWidth: "150px" },
              { name: "FUEL", selector: (row) => `PKR ${Number(row.fuel_expense || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, minWidth: "150px" },
              { name: "TOLLS", selector: (row) => `PKR ${Number(row.tolls_expense || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, minWidth: "120px" },
              { name: "MAINTENANCE", selector: (row) => `PKR ${Number(row.maintenance_expense || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, minWidth: "150px" },
              {
                name: "CONTRIBUTION", selector: (row) => (
                  <span style={{ color: Number(row.net_profit || 0) < 0 ? "#B42318" : "#027A48", fontWeight: 600 }}>
                    PKR {Number(row.net_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                ), minWidth: "150px"
              },
              {
                name: "MARGIN", selector: (row) => (
                  <span style={{ color: Number(row.profit_margin || 0) < 0 ? "#B42318" : "#027A48", fontWeight: 600 }}>
                    {Number(row.profit_margin || 0).toFixed(1)}%
                  </span>
                ), minWidth: "100px"
              }
            ],
            tableData: (reportDetail.pnl_customer_detail || []).map(item => ({
              customerName: item.customer?.name || `Customer #${item.customer_id}`,
              revenue: item.revenue,
              fuel_expense: item.fuel_expense,
              tolls_expense: item.tolls_expense,
              maintenance_expense: item.maintenance_expense,
              net_profit: item.net_profit,
              profit_margin: item.profit_margin
            })),
            tableNote: "Paid Revenue comes from recorded payments only. Unpaid invoice amounts are not counted as revenue.",
            companySummary: {
              title: "Company-Wide Summary",
              rows: [
                { label: "Total Paid Revenue", value: `PKR ${Number(reportDetail.total_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, isGreen: true },
                { label: "- Total Costs (Fuel + Toll + Maintenance)", value: `PKR ${Number(reportDetail.total_expense || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                { label: "Net Profit", value: `PKR ${Number(reportDetail.net_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, isBlue: true }
              ],
              note: "Revenue is recognized when payment is recorded."
            }
          };

          if (isMounted) {
            setGeneratedReport(dynamicReport);
          }
          return;
        }

        let report = null;
        try {
          const resAr = await apiService.getFinancialArReportsPaginated({ page: 1, limit: 100 });
          const listAr = resAr?.data?.results || resAr?.results || [];
          report = listAr.find((r) => String(r.id) === String(id));
          if (report) {
            report.report_type = "AR";
          }
        } catch (e) {
          console.warn("Failed to fetch AR reports list", e);
        }

        if (!isMounted) return;

        if (!report) {
          errorNotify("Report not found in history.");
          navigate("/finance-reports");
          return;
        }

        let reportType = "pnl";
        const typeVal = String(report.report_type || "").toLowerCase();
        if (typeVal.includes("ar") || typeVal.includes("receivable") || typeVal.includes("invoice")) {
          reportType = "ar";
        } else if (typeVal.includes("ap") || typeVal.includes("payable") || typeVal.includes("expense")) {
          reportType = "ap";
        }

        const rawStart = report.date_from || report.start_date || report.start_at || "";
        const rawEnd = report.date_to || report.end_date || report.end_at || "";
        const creatorName = report.user?.username || report.user?.name || report.created_by_name || "Admin";

        const dummy = generateDummyReportData(
          reportType,
          rawStart ? moment(rawStart).format("YYYY-MM-DD") : "",
          rawEnd ? moment(rawEnd).format("YYYY-MM-DD") : "",
          report.source_id || report.customer_id,
          creatorName
        );

        setGeneratedReport(dummy);
      } catch (error) {
        if (isMounted) {
          errorNotify(error || "Failed to fetch report details");
          navigate("/finance-reports");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReportDetails();

    return () => {
      isMounted = false;
    };
  }, [id, navigate]);

  const handleExportCSV = () => {
    if (!generatedReport) return;

    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += `"${generatedReport.title}"\n`;
    csvContent += `"${generatedReport.range}"\n\n`;

    generatedReport.cards.forEach((c) => {
      csvContent += `"${c.label}","${c.amount}"\n`;
    });
    csvContent += "\n";

    const headers = generatedReport.type === "pnl"
      ? ["Customer", "Paid Revenue", "Fuel", "Tolls", "Maintenance", "Contribution", "Margin"]
      : generatedReport.type === "ar"
        ? ["Customer", "Outstanding Amount", "1-30 Days", "31-60 Days", "61-90 Days", "Over 90 Days", "Last Invoiced Date"]
        : ["Supplier", "Total Payable", "Fuel Purchase", "Vehicle Maintenance", "Driver Payouts", "Due Date"];

    csvContent += headers.map((h) => `"${h}"`).join(",") + "\n";

    generatedReport.tableData.forEach((row) => {
      let rowVals = [];
      if (generatedReport.type === "pnl") {
        rowVals = [row.customer, row.paidRevenue, row.fuel, row.tolls, row.maintenance, row.contribution, row.margin];
      } else if (generatedReport.type === "ar") {
        rowVals = [row.customer, row.outstanding, row.days1_30, row.days31_60, row.days61_90, row.daysOver90, row.lastInvoiced];
      } else {
        rowVals = [row.supplier, row.outstanding, row.fuel, row.maintenance, row.payouts, row.dueDate];
      }
      csvContent += rowVals.map((val) => `"${val}"`).join(",") + "\n";
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${generatedReport.typeLabel.toLowerCase()}_report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    successNotify("Report exported as CSV successfully");
  };

  const handleExportPDF = async () => {
    if (!id || pdfDownloading) return;
    setPdfDownloading(true);
    successNotify("Generating PDF export...");

    try {
      const payload = {
        financial_pnl_report_master_id: Number(id),
      };
      
      const response = await apiService.exportCustomerPnlPdf(payload);
      
      const filePath =
        response?.data?.file_path ||
        response?.data?.filePath ||
        response?.data?.url ||
        response?.data ||
        response?.file_path ||
        response?.filePath ||
        response?.url ||
        "";
      const fileUrl = resolveApiFileUrl(filePath);

      if (!fileUrl) {
        errorNotify("PDF export file path not resolved");
        return;
      }

      const anchor = document.createElement("a");
      anchor.href = fileUrl;
      anchor.target = "_blank";
      anchor.download = `${generatedReport?.typeLabel || "report"}_details_${id}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      successNotify("Report PDF exported successfully");
    } catch (error) {
      errorNotify(error || "Failed to export PDF");
    } finally {
      setPdfDownloading(false);
    }
  };

  if (loading) {
    return <SpinnerLoader />;
  }

  if (!generatedReport) {
    return null;
  }

  return (
    <div className="trip_cost_estimation_page invoices_page">
      <Breadcrumbs values={["Finance", "Reports", generatedReport.typeLabel, "Details"]} />

      <div className="access_view">
        <h2>{generatedReport.title}</h2>

        <div className="d-flex gap-2">
          <button className="reports_btn_back" onClick={() => navigate(`/finance-reports?tab=${generatedReport.type}`)}>
            Back
          </button>
          {generatedReport.type === "pnl" && (
            <button className="reports_btn_pdf" onClick={handleExportPDF} disabled={pdfDownloading}>
              {pdfDownloading ? "Exporting..." : "Export PDF"}
            </button>
          )}
        </div>
      </div>

      <div style={{ paddingLeft: "24px", paddingRight: "24px", paddingTop: "16px" }}>
        <div className="reports_detail_cards_grid">
          {generatedReport.cards.map((c, idx) => (
            <div className="reports_detail_card" key={idx}>
              <div className="reports_detail_card_left">
                <span className="reports_detail_card_label">{c.label}</span>
                <span className="reports_detail_card_amount">{c.amount}</span>
                <span className="reports_detail_card_subtitle">{c.subtitle}</span>
              </div>
              <div className="reports_detail_card_icon">{c.icon}</div>
            </div>
          ))}
        </div>

        {generatedReport.warnings && generatedReport.warnings.map((w, idx) => (
          <div className="reports_warning_banner" key={idx}>
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FiAlertTriangle size={16} />
              {w.message}
            </span>
          </div>
        ))}

        <div className="financial_reports_card mb-4" style={{ marginLeft: 0, marginRight: 0 }}>
          <div className="financial_reports_card_header mb-3">
            <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#3D3F5C" }}>
              {generatedReport.tableTitle}
            </h3>
          </div>
          <NewTableComponent
            showAddBtn={false}
            showFilter={false}
            showPagination={false}
            columns={generatedReport.tableColumns}
            data={generatedReport.tableData}
            tableClassName="invoices_table parts_table_fix financial_reports_table"
          />
          {generatedReport.tableNote && (
            <p style={{ color: "#64748B", fontSize: "12px", marginTop: "16px", marginBottom: 0 }}>
              {generatedReport.tableNote}
            </p>
          )}
        </div>

        {generatedReport.companySummary && (
          <div className="company_summary_box mb-4">
            <div className="company_summary_title">
              {generatedReport.companySummary.title}
            </div>
            <div style={{ border: "1px solid #ECECF2", borderRadius: "8px", overflow: "hidden", marginBottom: "16px" }}>
              {generatedReport.companySummary.rows.map((r, idx) => (
                <div
                  className={`company_summary_row ${r.isIndented ? "indented" : ""}`}
                  key={idx}
                  style={{
                    borderBottom: idx < generatedReport.companySummary.rows.length - 1 ? "1px solid #ECECF2" : "none",
                    background: r.isBlue ? "#F8FAFC" : "#ffffff",
                    padding: "12px 24px"
                  }}
                >
                  <span className={`company_summary_label ${r.isIndented ? "sub" : ""}`}>
                    {r.label}
                  </span>
                  <span className={`company_summary_value ${r.isGreen ? "green" : r.isBlue ? "blue" : ""}`}>
                    {r.value}
                  </span>
                </div>
              ))}
            </div>
            {generatedReport.companySummary.note && (
              <div className="reports_success_banner">
                <span style={{ fontSize: "14px" }}>
                  {generatedReport.companySummary.note}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialReportDetails;
